import { useTranslation } from 'react-i18next';

interface Props {
  allMoods: string[];
  allGenres: string[];
  selectedMood: string | null;
  selectedGenres: string[];
  onSelectMood: (tag: string | null) => void;
  onSelectGenre: (tags: string[]) => void;
  availableMoods?: string[];
  availableGenres?: string[];
}

export default function TagSelector({
  allMoods,
  allGenres,
  selectedMood,
  selectedGenres,
  onSelectMood,
  onSelectGenre,
  availableMoods,
  availableGenres,
}: Props) {
  const { t } = useTranslation();

  return (
    <div>
      {/* Mood — single select */}
      <div className="mb-4 md:mb-6">
        <h3 className="text-sm md:text-base font-semibold mb-2 md:mb-3 flex items-center gap-3 text-[#F2BBB7]">
          <span className="w-2.5 h-2.5 rounded-full bg-current flex-shrink-0" />
          {t('mood')}
        </h3>
        <div className="flex flex-wrap gap-1.5 md:gap-3">
          {allMoods.map((tag) => {
            const isSelected = selectedMood === tag;
            const isDisabled =
              availableMoods != null && !isSelected && !availableMoods.includes(tag);
            return (
              <button
                key={tag}
                onClick={() => !isDisabled && onSelectMood(isSelected ? null : tag)}
                className={`px-3 py-2 md:px-5 md:py-3 rounded-lg md:rounded-xl text-xs md:text-base font-medium border transition-all duration-200 hover:-translate-y-px ${
                  isDisabled
                    ? 'bg-white/5 border-white/5 text-gray-600 cursor-not-allowed'
                    : isSelected
                      ? 'border-transparent text-[#040B13] shadow-[0_4px_15px_rgba(242,187,183,0.4)]'
                      : 'bg-white/5 border-white/10 text-gray-300 hover:border-[#F2BBB7]'
                }`}
                style={
                  isSelected
                    ? { background: 'linear-gradient(135deg, #F2BBB7, #CDA9E8)' }
                    : undefined
                }
              >
                {t(`tagNames.${tag}`, { defaultValue: tag })}
              </button>
            );
          })}
        </div>
      </div>

      {/* Genre — multi select */}
      <div className="mb-4 md:mb-6">
        <h3 className="text-sm md:text-base font-semibold mb-2 md:mb-3 flex items-center gap-3 text-[#8E98DF]">
          <span className="w-2.5 h-2.5 rounded-full bg-current flex-shrink-0" />
          {t('genre')}
        </h3>
        <div className="flex flex-wrap gap-1.5 md:gap-3">
          {allGenres.map((tag) => {
            const isSelected = selectedGenres.includes(tag);
            const isDisabled =
              availableGenres != null && !isSelected && !availableGenres.includes(tag);
            return (
              <button
                key={tag}
                onClick={() => {
                  if (isDisabled) return;
                  onSelectGenre(
                    isSelected ? selectedGenres.filter((g) => g !== tag) : [...selectedGenres, tag],
                  );
                }}
                className={`px-3 py-2 md:px-5 md:py-3 rounded-lg md:rounded-xl text-xs md:text-base font-medium border transition-all duration-200 hover:-translate-y-px ${
                  isDisabled
                    ? 'bg-white/5 border-white/5 text-gray-600 cursor-not-allowed'
                    : isSelected
                      ? 'border-transparent text-[#040B13] shadow-[0_4px_15px_rgba(142,152,223,0.4)]'
                      : 'bg-white/5 border-white/10 text-gray-300 hover:border-[#8E98DF]'
                }`}
                style={
                  isSelected
                    ? { background: 'linear-gradient(135deg, #8E98DF, #CDA9E8)' }
                    : undefined
                }
              >
                {t(`tagNames.${tag}`, { defaultValue: tag })}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
